import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import useColors from 'hooks/useColors';
import { AppView, Button, CollapsableContainer, rem, SCROLL_INDICATOR_INSETS, Spacer, TextButton, withAnimatedNavigationHeader, } from 'design-system';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserId } from 'reducers/selectors';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Animated from 'react-native-reanimated';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Text from 'design-system/Text';
import { setPotSpace } from 'features/pots/actions';
import { setTradingAccountSpace } from 'actions/invest';
import { forceRefreshAnalytics, getBankConnections, getCategories, getFeed, } from 'actions';
import RadioButton from 'design-system/RadioButton';
import { selectDefaultSpace } from './reducers/selectors';
import { editSpaceInfoSuccess, fetchSpacesAction, setAccountSpace, } from './actions';
import { AccountItem } from './components';
import { isWeb } from '../../constants';
import { useMySpaceAccounts } from './hooks/useMySpaceAccounts';
const SpaceUnavailableSelectMoveAccountsScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { spaceInfo } = route.params;
    const colors = useColors();
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const { mySpaceAccounts } = useMySpaceAccounts();
    const defaultSpace = useAppSelector(selectDefaultSpace);
    const userId = useAppSelector(selectUserId);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [selectedAccountsMap, setSelectedAccountsMap] = useState({});
    const [isMovingAccounts, setIsMovingAccounts] = useState(false);
    const spaces = useMemo(() => mySpaceAccounts.filter(({ space }) => space.createdBy === userId && !space.isDefault), [mySpaceAccounts, userId]);
    const onMoveAccounts = useCallback(async () => {
        if (!defaultSpace?.id)
            return;
        if (!selectedAccounts.length) {
            navigation.pop(3);
            navigation.navigateDeprecated('SpaceUnavailableLeaveAccounts', {
                spaceInfo,
            });
            return;
        }
        setIsMovingAccounts(true);
        const promises = [];
        promises.push(...selectedAccounts
            .filter((acc) => acc.userId === userId)
            .map((account) => {
            if ('productId' in account) {
                return dispatch(setPotSpace(String(account.id), defaultSpace.id));
            }
            if ('isEmmaInvestmentAccount' in account) {
                return dispatch(setTradingAccountSpace(defaultSpace.id, account.id));
            }
            return dispatch(setAccountSpace(account.id, defaultSpace.id));
        }));
        try {
            await Promise.all(promises);
            dispatch(fetchSpacesAction());
            dispatch(getBankConnections());
            dispatch(forceRefreshAnalytics());
            dispatch(getCategories());
            dispatch(getFeed());
            dispatch(editSpaceInfoSuccess(defaultSpace.id));
            selectedAccounts.forEach(({ spaceId }) => {
                if (spaceId)
                    dispatch(editSpaceInfoSuccess(spaceId));
            });
            navigation.pop(3);
            navigation.navigateDeprecated('SpaceUnavailableAccountsMoved', {
                spaceInfo,
            });
            setIsMovingAccounts(false);
        }
        catch {
            setIsMovingAccounts(false);
        }
    }, [defaultSpace?.id, selectedAccounts, spaceInfo, userId]);
    const onSelect = useCallback((account) => {
        if (selectedAccountsMap[account.id]) {
            setSelectedAccountsMap((prev) => ({
                ...prev,
                [account.id]: false,
            }));
            setSelectedAccounts((prev) => prev.filter(({ id }) => account.id !== id));
        }
        else {
            setSelectedAccountsMap((prev) => ({ ...prev, [account.id]: true }));
            setSelectedAccounts((prev) => [...prev, account]);
        }
    }, [selectedAccountsMap]);
    const allAccounts = useMemo(() => spaces.map(({ accounts }) => accounts).flat(), [spaces]);
    const isAllSelected = allAccounts.length === selectedAccounts.length;
    const onToggleSelected = useCallback(() => {
        if (isAllSelected) {
            setSelectedAccountsMap({});
            setSelectedAccounts([]);
        }
        else {
            setSelectedAccountsMap(allAccounts.reduce((acc, curr) => ({ ...acc, [curr.id]: true }), {}));
            setSelectedAccounts(allAccounts);
        }
    }, [allAccounts, isAllSelected]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
            <ScreenInfoHeader title="Select accounts to move"/>
            <View style={styles.sectionTitle}>
              <Text Text-16 Secondary>
                Accounts in your spaces
              </Text>
              <TextButton onPress={onToggleSelected} text={isAllSelected ? 'Deselect all' : 'Select all'}/>
            </View>
            {/* TODO: use FlashList/FlatList */}
            {spaces.map((item) => item.accounts.length > 0 ? (<CollapsableContainer key={item.space.id} title={item.space.name} style={styles.itemContainer} isCollapsedByDefault={false}>
                  {item.accounts.map((account) => (<TouchableHighlight onCard hoverOnLight underlayColor={colors.background.underlay} activeOpacity={1} onPress={() => onSelect(account)} key={account.id}>
                      <View style={styles.accountItem}>
                        <RadioButton type="multiple" isChecked={selectedAccountsMap[account.id]}/>
                        {!!isWeb && <Spacer.W8 />}
                        <View style={styles.flex}>
                          <AccountItem hideSpaceUserAvatar account={account} disabled/>
                        </View>
                      </View>
                    </TouchableHighlight>))}
                </CollapsableContainer>) : null)}
          </Animated.ScrollView>
          <Button brand floating title="Continue" onPress={onMoveAccounts} isFetching={isMovingAccounts}/>
        </AppView>);
}, ({ colors }) => ({
    title: 'Move accounts',
    hasCustomTitle: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    sectionTitle: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginHorizontal: rem(16),
        marginBottom: rem(16),
        gap: rem(16),
    },
    itemContainer: {
        marginHorizontal: rem(16),
        marginBottom: rem(16),
    },
    accountItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: rem(isWeb ? 8 : 16),
    },
    flex: {
        flex: 1,
    },
});
export default SpaceUnavailableSelectMoveAccountsScreen;
